import { MsgExecuteContractCompat } from '@injectivelabs/sdk-ts'
import { msgBroadcaster } from '@shared/WalletService'
import { sharedToBalanceInWei } from '@shared/utils/formatter'
import { STAKING_CONTRACT_ADDRESS } from '@/app/utils/constant'
import { backupPromiseCall } from '@/app/client/utils/async'
import { UiVault } from '@/types'

export const stake = async ({
  vault,
  lpTokenAmount
}: {
  vault: UiVault
  lpTokenAmount: string
}) => {
  const walletStore = useWalletStore()
  const stakingStore = useStakingStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    sender: sharedWalletStore.injectiveAddress,
    funds: [
      {
        denom: vault.lpDenom,
        amount: sharedToBalanceInWei({ value: lpTokenAmount }).toFixed()
      }
    ],
    contractAddress: STAKING_CONTRACT_ADDRESS,
    exec: {
      action: 'stake',
      msg: {}
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  backupPromiseCall(() => stakingStore.fetchStakingRewardsByAccount())

  return tx
}

export const unstake = async ({
  vault,
  lpTokenAmount
}: {
  vault: UiVault
  lpTokenAmount: string
}) => {
  const walletStore = useWalletStore()
  const stakingStore = useStakingStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    sender: sharedWalletStore.injectiveAddress,
    contractAddress: STAKING_CONTRACT_ADDRESS,
    exec: {
      action: 'unstake',
      msg: {
        coin: {
          denom: vault.lpDenom,
          amount: sharedToBalanceInWei({ value: lpTokenAmount }).toFixed()
        }
      }
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  backupPromiseCall(() => stakingStore.fetchStakingRewardsByAccount())

  return tx
}

export const claimAllRewards = async (lpDenoms: string[]) => {
  const walletStore = useWalletStore()
  const stakingStore = useStakingStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const msgs = lpDenoms.map((lpDenom) => {
    return MsgExecuteContractCompat.fromJSON({
      sender: sharedWalletStore.injectiveAddress,
      contractAddress: STAKING_CONTRACT_ADDRESS,
      exec: {
        action: 'claim_rewards',
        msg: { lp_token: lpDenom }
      }
    })
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs
  })

  backupPromiseCall(() => stakingStore.fetchStakingRewardsByAccount())

  return tx
}

export const claimRewards = async (lpDenom: string) => {
  const walletStore = useWalletStore()
  const stakingStore = useStakingStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    sender: sharedWalletStore.injectiveAddress,
    contractAddress: STAKING_CONTRACT_ADDRESS,
    exec: {
      action: 'claim_rewards',
      msg: {
        lp_token: lpDenom
      }
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  backupPromiseCall(() => stakingStore.fetchStakingRewardsByAccount())

  return tx
}

export const claimStake = async (lpDenom: string) => {
  const mitoStore = useMitoStore()
  const walletStore = useWalletStore()
  const stakingStore = useStakingStore()
  const sharedWalletStore = useSharedWalletStore()

  if (
    !sharedWalletStore.isUserConnected ||
    !sharedWalletStore.defaultSubaccountId
  ) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    sender: sharedWalletStore.injectiveAddress,
    contractAddress: STAKING_CONTRACT_ADDRESS,
    exec: {
      action: 'claim_stake',
      msg: {
        lp_token: lpDenom
      }
    }
  })

  const tx = await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  backupPromiseCall(() =>
    Promise.all([
      mitoStore.fetchHolderPortfolio(),
      stakingStore.fetchStakingRewardsByAccount()
    ])
  )

  return tx
}
